import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'debounce-promise-with-cancel';
import { EmbedDynamicConstants, WithApp } from './context/AppContext';
import { notUndefined } from '../../../../../../shared/lib/functional';
import { EmbedConfig, EmbedView } from '../..';
import Calendar from './views/Calendar';
import './styles/style.scss';
import Reserve from './views/Reserve';

interface AppPropsBase {
  config: EmbedConfig;
}

export type AppProps = AppPropsBase & React.HTMLProps<HTMLDivElement>;

let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
window.GO_IS_SCROLLING = false;

function App(props: AppProps) {
  const { config, className, ...rest } = props;

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [lastScroll, setLastScroll] = useState<string | undefined>();
  const [currentView, setCurrentView] = useState<EmbedView>(config.enabledViews[0] || 'calendar');

  const stickyDiv = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!stickyDiv.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setWindowWidth(window.innerWidth);
    });

    resizeObserver.observe(stickyDiv.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  function onScroll() {
    const st = window.pageYOffset || document.documentElement.scrollTop;
    if (st > lastScrollTop) setLastScroll('down');
    else if (st < lastScrollTop) setLastScroll('up');
    lastScrollTop = st <= 0 ? 0 : st;
    window.GO_IS_SCROLLING = true;
    setNotScrollingDebounced();
  }

  const setNotScrollingDebounced = debounce(setNotScrolling, config.animationDuration * 1.5);

  async function setNotScrolling() {
    window.GO_IS_SCROLLING = false;
  }

  const isMobile = windowWidth < 620;

  const classes = ['text-base border-t border-b', className];

  if (isMobile) classes.push('go-is-mobile');
  else classes.push('border-l border-r shadow go-is-desktop');
  if (lastScroll) classes.push(`go-last-scroll-${lastScroll}`);

  const navigationItems = config.enabledViews
    .map(v =>
      v === 'calendar'
        ? {
            name: 'calendar' as EmbedView,
            render: (key?: string) => (
              <button
                key={key}
                className={`text-2xl basic-button 
                  ${currentView === 'calendar' ? 'text-accent active font-bold' : 'text-active'} go-view-toggle`}
                onClick={() => setCurrentView('calendar')}
              >
                Calendar
              </button>
            ),
          }
        : v === 'reserve'
          ? {
              name: 'reserve' as EmbedView,
              render: (key?: string) => (
                <button
                  key={key}
                  className={`text-2xl basic-button
                    ${currentView === 'reserve' ? 'text-accent active font-bold' : 'text-active'} go-view-toggle`}
                  onClick={() => setCurrentView('reserve')}
                >
                  Experiences
                </button>
              ),
            }
          : undefined
    )
    .filter(notUndefined);

  const dynamicConstants: EmbedDynamicConstants = {
    isMobile,
    stickyDiv,
  };

  return (
    <div id="go-app" ref={stickyDiv} className={classes.join(' ')} {...rest}>
      <WithApp config={props.config} dynamicConstants={dynamicConstants}>
        <div className="flex flex-wrap gap-x-4 items-center justify-center p-4 go-nav-container">
          {navigationItems.map(item => item.render(item.name))}
        </div>
        {currentView === 'calendar' && <Calendar />}
        {currentView === 'reserve' && <Reserve />}
      </WithApp>
    </div>
  );
}

export default App;
